/*
 * Axis snap — keeps thin decorative lines exactly the same on screen: the phase axis line of every dwm-expertises
 * section (.dwm-exp__axis-line) and the small ticks in front of the dwm-worklog items (.dwm-worklog__tick).
 *
 * Why: a line lands on a fractional position on the page (text above it has fractional line heights), and at most
 * zoom levels one CSS pixel is not a whole screen pixel either. The browser then draws a 2 px line over two rows in
 * one place and smeared over three rows in the next, so one line looks thicker than the other.
 *
 * Every line is a rect inside a small SVG (1 SVG unit = 1 CSS px). This sets the rect's position so the line starts
 * on a whole screen pixel, and its size to the whole number of screen pixels nearest to the design size. SVG
 * coordinates are not rounded by layout, so every line gets exactly the same pixel phase and renders identically.
 * Re-runs when the layout, the fonts or the pixel ratio change. Without JavaScript the lines keep their design size.
 */

const TARGETS = [
  // The phase axis: a 2 px line centred in an 8 px track; only the vertical position is snapped.
  { selector: '.dwm-exp__axis-line', height: 2, centerY: 4, snapX: false, arrow: '.dwm-exp__axis-arrow' },
  // Worklog ticks: an 8 × 2 px line centred in an 8 × 8 px box; snapped both ways.
  { selector: '.dwm-worklog__tick', height: 2, centerY: 4, snapX: true, width: 8 },
];

let frame = 0;

function snap() {
  frame = 0;
  const dpr = window.devicePixelRatio || 1;
  const toScreen = (value) => Math.round(value * dpr) / dpr;
  for (const target of TARGETS) {
    const height = Math.max(1, Math.round(target.height * dpr)) / dpr;
    const width = target.snapX ? Math.max(1, Math.round(target.width * dpr)) / dpr : 0;
    for (const svg of document.querySelectorAll(target.selector)) {
      const rect = svg.querySelector('rect');
      if (!rect || !svg.getClientRects().length) continue; // hidden (e.g. mobile layout)
      const box = svg.getBoundingClientRect();
      const svgTop = box.top + window.scrollY;
      const lineTop = toScreen(svgTop + target.centerY - height / 2);
      rect.setAttribute('y', String(lineTop - svgTop));
      rect.setAttribute('height', String(height));
      // The phase axis arrowhead (same 8 px track, drawn around y = centerY) follows the snapped line exactly.
      const arrow = target.arrow && svg.parentElement.querySelector(target.arrow);
      if (arrow) {
        const arrowTop = arrow.getBoundingClientRect().top + window.scrollY;
        const shift = lineTop + height / 2 - (arrowTop + target.centerY);
        for (const shape of arrow.querySelectorAll('path')) shape.setAttribute('transform', `translate(0 ${shift})`);
      }
      if (target.snapX) {
        const svgLeft = box.left + window.scrollX;
        rect.setAttribute('x', String(toScreen(svgLeft) - svgLeft));
        rect.setAttribute('width', String(width));
      }
    }
  }
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(snap);
}

function watchPixelRatio() {
  window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`).addEventListener('change', () => {
    schedule();
    watchPixelRatio();
  }, { once: true });
}

if (!window.dwmAxisSnap) {
  window.dwmAxisSnap = true;
  schedule();
  new ResizeObserver(schedule).observe(document.documentElement);
  document.fonts?.ready.then(schedule);
  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule); // browser zoom and window resizes, also when the page size stays equal
  document.addEventListener('shopify:section:load', schedule);
  watchPixelRatio();
}
